import type { Outcome, ProbabilityPick } from "../../shared/types";
import { formatKickoff, formatPercent, formatSignedPercent } from "../lib/format";
import { Badge, type BadgeTone } from "./Badge";
import { ConfidenceChip } from "./ConfidenceChip";
import { PickProvenance } from "./PickProvenance";
import { Stat } from "./Stat";
import { VerdictBadge } from "./VerdictBadge";

const OUTCOME_LABELS: Record<Outcome, string> = {
  home: "Home",
  draw: "Draw",
  away: "Away",
};

export function PickRow({ pick, rank }: { pick: ProbabilityPick; rank: number }) {
  const marginTone = marginBadgeTone(pick.match.margin);
  const evTone = evBadgeTone(pick.evScore);
  const modelEvTone = evBadgeTone(pick.modelEv);

  return (
    <details
      className={`panel group/row overflow-hidden ${pick.highConviction ? "ring-2 ring-emerald-400" : ""}`}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-2.5 py-1.5 marker:hidden sm:gap-3 sm:px-3 [&::-webkit-details-marker]:hidden">
        <span className="w-6 shrink-0 text-center text-xs font-medium text-ink-500">#{rank}</span>
        <span className="w-12 shrink-0 rounded bg-ink-100 px-1.5 py-0.5 text-center text-[11px] font-medium text-ink-700">
          {OUTCOME_LABELS[pick.outcome]}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-sm font-semibold text-ink-900">{pick.selectionName}</p>
            <VerdictBadge verdict={pick.verdict} />
            <ConfidenceChip confidence={pick.confidence} />
            {pick.leagueId ? (
              <Badge
                tone="neutral"
                title={`Competition: ${pick.leagueLabel ?? pick.leagueId}. We only show picks from a curated whitelist of trustworthy leagues.`}
              >
                {pick.leagueId}
              </Badge>
            ) : null}
            {marginTone ? (
              <Badge
                tone={marginTone}
                title={`Bookmaker's mark-up on this 1X2 market: ${formatPercent(pick.match.margin)}. Lower = more honest pricing. Picks above 8% are filtered out.`}
              >
                {formatPercent(pick.match.margin)} vig
              </Badge>
            ) : null}
            {evTone && pick.evScore !== undefined ? (
              <Badge
                tone={evTone}
                title={`Expected profit per €1 staked, vs sharp-book consensus. ${pick.evScore > 0 ? "Positive = bet looks mispriced in your favor." : "Negative = you're paying the house margin."}`}
              >
                {formatSignedPercent(pick.evScore)} EV
              </Badge>
            ) : null}
            {modelEvTone && pick.modelEv !== undefined ? (
              <Badge
                tone={modelEvTone}
                title="Expected value from our independent Dixon-Coles model (built from goal-scoring base rates × Elo strength × recent form). Compare with the consensus EV — when both are positive AND agree, this becomes a high-conviction pick."
              >
                {formatSignedPercent(pick.modelEv)} MODEL
              </Badge>
            ) : null}
            {pick.weather && pick.weather.severity >= 0.4 ? (
              <Badge
                tone="warn"
                title={`Weather at kickoff: ${pick.weather.conditionDescription}, ${pick.weather.temperatureC.toFixed(0)}°C, wind ${pick.weather.windSpeedMs.toFixed(1)} m/s, precipitation ${pick.weather.precipMm.toFixed(1)} mm. Tough conditions tend to reduce goals.`}
              >
                {weatherEmoji(pick.weather.conditionMain)} {pick.weather.temperatureC.toFixed(0)}°
              </Badge>
            ) : null}
          </div>
          <p className="truncate text-[11px] text-ink-500">
            {formatKickoff(pick.match.kickoffTime)} · {pick.match.homeTeam} vs {pick.match.awayTeam}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-right text-xs sm:gap-4">
          <Stat
            label="Odds"
            value={pick.decimalOdds.toFixed(2)}
            description="What Norsk Tipping pays out per €1 staked. 2.00 means €1 in → €2 back (€1 profit). Higher = the book thinks it's less likely to happen."
          />
          <Stat
            className="hidden sm:block"
            label="Fair"
            value={pick.fairOdds.toFixed(2)}
            description="What the odds would be if the bookmaker took zero profit margin. Compare to the Odds column: a big gap means the book is taking more than usual on this market."
          />
          <Stat
            label="Prob"
            value={formatPercent(pick.fairProbability)}
            description="Our best estimate of the true chance this pick wins, derived from the book's prices with their margin stripped off. 60% = we think it happens roughly 6 times in 10."
          />
          {pick.modelFairProbability !== undefined ? (
            <Stat
              className="hidden md:block"
              label="Model"
              value={formatPercent(pick.modelFairProbability)}
              description="Independent Dixon-Coles model probability — built from historical league base rates, Elo strength, and recent form. Doesn't use NT's prices at all. When this disagrees with Prob, the bookmaker may be mispricing."
            />
          ) : null}
          {pick.evScore !== undefined ? (
            <Stat
              className="hidden sm:block"
              label="EV"
              value={formatSignedPercent(pick.evScore)}
              description="Expected profit or loss per €1 staked, compared to a consensus of sharp bookmakers. Positive = the bet is mispriced in your favor (rare on NT). Negative = you're paying the house margin (normal)."
            />
          ) : null}
          <Chevron />
        </div>
      </summary>
      <PickProvenance pick={pick} />
    </details>
  );
}

function weatherEmoji(conditionMain: string): string {
  switch (conditionMain.toLowerCase()) {
    case "rain":
    case "drizzle":
      return "🌧";
    case "thunderstorm":
      return "⛈";
    case "snow":
      return "❄";
    case "clear":
      return "☀";
    case "clouds":
      return "☁";
    case "mist":
    case "fog":
    case "haze":
      return "🌫";
    default:
      return "⛅";
  }
}

function Chevron() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className="ml-0.5 h-3 w-3 shrink-0 text-ink-500 transition-transform group-open/row:rotate-180"
    >
      <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function marginBadgeTone(margin: number): BadgeTone | null {
  if (margin <= 0.06) return "good";
  if (margin <= 0.08) return "warn";
  return null;
}

function evBadgeTone(ev: number | undefined): BadgeTone | null {
  if (ev === undefined) return null;
  if (ev > 0.005) return "good";
  if (ev < -0.05) return "bad";
  return "neutral";
}
